/** Where the emailed sign-in link lands. Asks for the email only when opened on another device. */

import { useEffect, useRef, useState, type FormEvent } from "react";
import { Navigate } from "react-router";

import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function FinishSignInPage() {
  const { state, finishSignInLink } = useAuth();
  const [phase, setPhase] = useState<"working" | "need-email" | "not-a-link" | "failed">("working");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const attempted = useRef(false);

  useEffect(() => {
    if (state.status !== "signed-out" || attempted.current) return;
    attempted.current = true;
    finishSignInLink(window.location.href, null)
      .then((outcome) => outcome !== "signed-in" && setPhase(outcome))
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "the link could not be used");
        setPhase("failed");
      });
  }, [state.status, finishSignInLink]);

  if (state.status === "signed-in") return <Navigate to="/" replace />;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    try {
      await finishSignInLink(window.location.href, email.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : "the link could not be used");
      setPhase("failed");
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-sm flex-col justify-center gap-4 px-4">
      {phase === "working" && <p className="text-muted-foreground">Signing you in…</p>}
      {phase === "need-email" && (
        <form onSubmit={onSubmit} className="space-y-3">
          <p className="text-sm text-muted-foreground">
            This link was opened on a different device. Confirm the email it was sent to.
          </p>
          <Label htmlFor="confirm-email">Email</Label>
          <Input id="confirm-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <Button type="submit" className="w-full">
            Continue
          </Button>
        </form>
      )}
      {(phase === "not-a-link" || phase === "failed") && (
        <div className="space-y-3">
          <p>This sign-in link is invalid or has expired.</p>
          {error && <p className="text-sm text-muted-foreground">{error}</p>}
          <Button onClick={() => window.location.assign("/login")}>Send a new link</Button>
        </div>
      )}
    </main>
  );
}
